import React, { useEffect, useMemo, useState } from "react";
import ApiData from "../../api/ApiData";
import { InputEditVer1 } from "../UI/InputEditVer1";
import {
  formatMoneyString,
  formatVatRate,
} from "../../utils/integration1cPriceDisplay";
import {
  buildLicenseProductSummaryLines,
  computeLicenseCalculatorTotals,
  filterLicenseCalculatorPrices,
  normalizeLicenseQuantity,
  priceRowKey,
} from "../../utils/license1cCalculatorUtils";

const DISCLAIMER_TEXT = "Расчёт носит исключительно информативный характер";

export function License1cCalculator({
  className = "",
  active = true,
  variant = "embedded",
}) {
  const [prices, setPrices] = useState([]);
  const [quantities, setQuantities] = useState({});
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!active) {
      return undefined;
    }

    let cancelled = false;
    setPrices([]);
    setQuantities({});
    setLoaded(false);
    setLoading(true);

    ApiData.getIntegration1cPrices({ kind: "license" })
      .then((list) => {
        if (cancelled) return;
        const filtered = filterLicenseCalculatorPrices(list ?? []);
        setPrices(filtered);
        const initialQty = {};
        for (const item of filtered) {
          const key = priceRowKey(item);
          if (key) {
            initialQty[key] = 0;
          }
        }
        setQuantities(initialQty);
        setLoaded(true);
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [active]);

  const productSummaryLines = useMemo(
    () => buildLicenseProductSummaryLines(prices, quantities),
    [prices, quantities],
  );

  const totals = useMemo(
    () => computeLicenseCalculatorTotals(prices, quantities),
    [prices, quantities],
  );

  const rootClass = [
    "license-1c-calculator",
    variant === "header" ? "license-1c-calculator--header" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  if (loading) {
    return (
      <div className={rootClass}>
        <span className="license-1c-calculator-status">Загрузка цен…</span>
      </div>
    );
  }

  if (!loaded) {
    return null;
  }

  if (!prices.length) {
    return (
      <div className={rootClass}>
        <span className="license-1c-calculator-status">Цены не найдены</span>
      </div>
    );
  }

  const onQuantityChange = (key, raw) => {
    setQuantities((prev) => ({
      ...prev,
      [key]: normalizeLicenseQuantity(raw),
    }));
  };

  const listClass =
    variant === "header"
      ? "license-1c-calculator-list license-1c-calculator-list--scroll"
      : "license-1c-calculator-list";

  return (
    <div className={rootClass}>
      <div className={listClass}>
        {prices.map((item) => {
          const key = priceRowKey(item);
          const qty = quantities[key] ?? 0;
          return (
            <div key={key} className="license-1c-calculator-block">
              <label className="flx-sb license-1c-calculator-row">
                <span className="license-1c-calculator-name">{item.name}</span>
                <InputEditVer1
                  type="number"
                  min={0}
                  value={qty}
                  onChange={(e) => onQuantityChange(key, e.target.value)}
                  aria-label={`Количество: ${item.name}`}
                />
              </label>
              <div className="flx-sb license-1c-calculator-row">
                <span className="site-form-label">НДС</span>
                <span className="license-1c-calculator-value">
                  {formatVatRate(item.vat_rate)}
                </span>
              </div>
              <div className="flx-sb license-1c-calculator-row">
                <span className="site-form-label">Цена с НДС</span>
                <span className="license-1c-calculator-value">
                  {formatMoneyString(item.price_with_vat)}
                </span>
              </div>
              <div className="flx-sb license-1c-calculator-row">
                <span className="site-form-label">Цена без НДС</span>
                <span className="license-1c-calculator-value">
                  {formatMoneyString(item.price_without_vat)}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      <footer className="license-1c-calculator-footer">
        {productSummaryLines.length > 0 ? (
          <div className="license-1c-calculator-summary">
            {productSummaryLines.map((line) => (
              <div
                key={line.key}
                className="flx-sb license-1c-calculator-summary-line"
              >
                <span className="license-1c-calculator-summary-name">
                  {line.name}
                </span>
                <span className="license-1c-calculator-summary-qty">
                  {line.qty}шт
                </span>
              </div>
            ))}
          </div>
        ) : null}
        <div className="flx-sb license-1c-calculator-total-row license-1c-calculator-total-row--primary">
          <span className="site-form-label">Итого с НДС</span>
          <span className="license-1c-calculator-total-value">
            {formatMoneyString(String(totals.totalWithVat))}
          </span>
        </div>
        <div className="flx-sb license-1c-calculator-total-row">
          <span className="site-form-label">Итого без НДС</span>
          <span className="license-1c-calculator-total-value">
            {formatMoneyString(String(totals.totalWithoutVat))}
          </span>
        </div>
        <p className="license-1c-calculator-disclaimer" role="note">
          {DISCLAIMER_TEXT}
        </p>
      </footer>
    </div>
  );
}
